import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import {
  ICommunication,
  ISendCommunicationRequest,
} from '../../../models/communication.model';
import { CommunicationsService } from '../../../services/communications.service';
import { SidenavService } from '../../../services/sidenav.service';
import { SnackbarService } from '../../../services/snackbar.service';

@Component({
  selector: 'app-communication-detail',
  templateUrl: './communication-detail.component.html',
  styleUrls: ['./communication-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunicationDetailComponent implements OnInit {
  @BlockUI() blockUI!: NgBlockUI;

  sidenav: MatSidenav;
  isNew = false;
  isEditing = false;
  communication: ICommunication | null = null;
  communicationId = '';

  draft: ISendCommunicationRequest = { subject: '', html: '', text: '' };

  tinymceConfig: Record<string, any> = {
    height: 450,
    menubar: false,
    plugins: ['code'],
    toolbar: 'bold italic underline | bullist numlist | link | code',
    content_style: 'body { font-family: sans-serif; font-size: 14px; }',
    promotion: false,
    branding: false,
  };

  constructor(
    private sidenavService: SidenavService,
    private communicationsService: CommunicationsService,
    private snackbar: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
  }

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id || id === 'new') {
      this.isNew = true;
      return;
    }
    this.communicationId = id;
    this.isEditing = this.route.snapshot.queryParamMap.get('mode') === 'edit';

    this.blockUI.start();
    try {
      this.communication = await this.communicationsService.getById(id);
      this.fillDraftFromCommunication();
    } catch {
      this.snackbar.error('No se ha podido cargar el comunicado.');
      this.router.navigate(['/admin/communications']);
    } finally {
      this.blockUI.stop();
      this.cdr.markForCheck();
    }
  }

  toggleMenu(): void {
    this.sidenavService.toggle();
  }

  goBack(): void {
    this.router.navigate(['/admin/communications']);
  }

  async create(form: NgForm): Promise<void> {
    if (form.invalid) return;

    this.blockUI.start();
    try {
      await this.communicationsService.create(this.draft);
      this.snackbar.success('Comunicado creado correctamente.');
      this.router.navigate(['/admin/communications']);
    } catch {
      this.snackbar.error('Error al crear el comunicado.');
    } finally {
      this.blockUI.stop();
    }
  }

  async update(form: NgForm): Promise<void> {
    if (form.invalid || !this.communicationId) return;

    this.blockUI.start();
    try {
      await this.communicationsService.update(this.communicationId, this.draft);
      this.snackbar.success('Comunicado actualizado correctamente.');
      this.isEditing = false;
      this.communication = await this.communicationsService.getById(
        this.communicationId,
      );
      this.fillDraftFromCommunication();
      this.cdr.markForCheck();
    } catch {
      this.snackbar.error('Error al actualizar el comunicado.');
    } finally {
      this.blockUI.stop();
    }
  }

  startEdit(): void {
    this.isEditing = true;
    this.fillDraftFromCommunication();
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.fillDraftFromCommunication();
  }

  openRatePercent(value: number, total: number): string {
    if (!total) return '0%';
    return ((value / total) * 100).toFixed(1) + '%';
  }

  private fillDraftFromCommunication(): void {
    if (!this.communication) return;
    this.draft = {
      subject: this.communication.subject || '',
      html: this.communication.html || '',
      text: this.communication.text || '',
    };
  }
}
